/**
 * The activity graph's picture (roadmap D240), DOM-free: where each node goes,
 * what the filters and the time scrubber keep, what a search finds, and the
 * SVG an export writes. The graph itself comes from the host (or, in the
 * organisation's console, from the platform) in the shape of
 * shared/schemas/activityGraph.ts; this file only reads it.
 *
 * Kept free of anything workbench-only (no bridge, no DOM, no app state) so
 * the console can carry the same file.
 *
 * The layout is layered, left to right: the run, its agents in turn order,
 * then one column per kind of thing they touched — tools, commands,
 * connectors, files read, files changed, questions and approvals, then
 * resets, hand-overs and verdicts. A leaf sits level with the agents that
 * touched it (their mean row), so lines cross as little as a column layout
 * allows. Node size is the count (an agent's, its tokens); colour and shape
 * are the kind, and a legend names both, so colour is never the only signal.
 */
import type { ActivityGraph, ActivityNode, ActivityNodeKind } from '@aioe/shared';

/** The words for each kind: a legend's (many) and a detail's (one). */
export const KIND_LABEL: Record<ActivityNodeKind, string> = {
  run: 'Run',
  agent: 'Agent',
  tool: 'Tools',
  'foundry-tool': 'Tools in Foundry',
  command: 'Commands',
  'file-read': 'Files read',
  'file-changed': 'Files changed',
  connector: 'Connectors',
  question: 'Questions',
  approval: 'Approvals',
  reset: 'Context resets',
  handover: 'Hand-overs',
  verdict: 'Verdicts',
};
export const KIND_ONE: Record<ActivityNodeKind, string> = {
  run: 'Run',
  agent: 'Agent',
  tool: 'Tool',
  'foundry-tool': 'Tool in Foundry',
  command: 'Command',
  'file-read': 'File read',
  'file-changed': 'File changed',
  connector: 'Connector',
  question: 'Question',
  approval: 'Approval',
  reset: 'Context reset',
  handover: 'Hand-over',
  verdict: 'Verdict',
};

// ── Kinds: colour, shape, filter group ──────────────────────────────────

export type NodeShape = 'roundrect' | 'circle' | 'square' | 'diamond' | 'hexagon' | 'triangle';

/**
 * The validated categorical palette (the dataviz reference instance, as the
 * workbench's `--plan-tone-*`), light and dark steps. Slots are assigned in
 * the palette's fixed order to the kinds a person tells apart most; the
 * run, agents and the three event kinds are ink, told apart by shape.
 * Concrete values, not CSS variables: the stage is dark whatever the theme,
 * and an exported image has no stylesheet.
 */
export const PALETTE = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
} as const;

/** Surfaces and ink for the picture (the stage's own on screen; the export's from the theme). */
export const CHROME = {
  light: { surface: '#fcfcfb', ink: '#0b0b0b', secondary: '#52514e', muted: '#898781', edge: '#c3c2b7', ring: 'rgba(11,11,11,0.10)' },
  dark: { surface: '#0b0f14', ink: '#f0efec', secondary: '#c3c2b7', muted: '#98a2b1', edge: '#4a5260', ring: 'rgba(255,255,255,0.14)' },
} as const;

export type GraphTheme = keyof typeof PALETTE;

/** Each kind's look: a palette slot (or ink), a shape, filled or outlined. */
export const KIND_STYLE: Record<ActivityNodeKind, { slot?: number; shape: NodeShape; outline?: boolean }> = {
  run: { shape: 'roundrect' },
  agent: { shape: 'circle' },
  tool: { slot: 0, shape: 'circle' },
  // Ran inside Foundry (D273): the agent definition allowed it, the gate never saw it. Its own colour and an outline.
  'foundry-tool': { slot: 7, shape: 'circle', outline: true },
  command: { slot: 1, shape: 'square' },
  'file-read': { slot: 2, shape: 'circle', outline: true },
  'file-changed': { slot: 2, shape: 'circle' },
  question: { slot: 3, shape: 'diamond' },
  approval: { slot: 4, shape: 'diamond', outline: true },
  connector: { slot: 6, shape: 'hexagon' },
  reset: { shape: 'square', outline: true },
  handover: { shape: 'triangle' },
  verdict: { slot: 5, shape: 'triangle', outline: true },
};

/** The colour a kind is drawn in, for a theme. */
export function kindColour(kind: ActivityNodeKind, theme: GraphTheme): string {
  const slot = KIND_STYLE[kind].slot;
  return slot === undefined ? (kind === 'run' || kind === 'agent' ? CHROME[theme].ink : CHROME[theme].secondary) : PALETTE[theme][slot];
}

/** What the filter row offers: a person thinks in these, not in twelve kinds. */
export const FILTER_GROUPS = [
  { id: 'tools', label: 'Tools', kinds: ['tool', 'foundry-tool'] },
  { id: 'commands', label: 'Commands', kinds: ['command'] },
  { id: 'files', label: 'Files', kinds: ['file-read', 'file-changed'] },
  { id: 'connectors', label: 'Connectors', kinds: ['connector'] },
  { id: 'questions', label: 'Questions', kinds: ['question', 'approval'] },
  { id: 'events', label: 'Resets and verdicts', kinds: ['reset', 'handover', 'verdict'] },
] as const satisfies ReadonlyArray<{ id: string; label: string; kinds: readonly ActivityNodeKind[] }>;
export type FilterGroupId = (typeof FILTER_GROUPS)[number]['id'];
export const ALL_GROUPS: ReadonlySet<FilterGroupId> = new Set(FILTER_GROUPS.map((g) => g.id));

/** The columns, left to right; kinds sharing a column are stacked in this order. */
export const COLUMNS: ReadonlyArray<readonly ActivityNodeKind[]> = [
  ['run'],
  ['agent'],
  ['tool'],
  ['foundry-tool'],
  ['command'],
  ['connector'],
  ['file-read'],
  ['file-changed'],
  ['question', 'approval'],
  ['reset', 'handover', 'verdict'],
];

// ── Filters, search, the time window ────────────────────────────────────

/** Whether a node shows under the chosen groups; the run and agents always do. */
export function kindShown(kind: ActivityNodeKind, groups: ReadonlySet<FilterGroupId>): boolean {
  if (kind === 'run' || kind === 'agent') return true;
  return FILTER_GROUPS.some((g) => groups.has(g.id) && (g.kinds as readonly ActivityNodeKind[]).includes(kind));
}

/** The nodes the picture draws under the filters, and the edges between two of them. */
export function visibleGraph(graph: ActivityGraph, groups: ReadonlySet<FilterGroupId>): { nodes: ActivityNode[]; edges: ActivityGraph['edges'] } {
  const nodes = graph.nodes.filter((n) => kindShown(n.kind, groups));
  const ids = new Set(nodes.map((n) => n.id));
  return { nodes, edges: graph.edges.filter((e) => ids.has(e.from) && ids.has(e.to)) };
}

/** A search over names: every word must appear (case-insensitive) in the label or detail. */
export function matchesSearch(node: ActivityNode, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return false;
  const hay = `${node.label} ${node.detail ?? ''} ${KIND_LABEL[node.kind]}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** The graph's time span in ms (never empty: a graph with no times spans its own from–to). */
export function timeSpan(graph: ActivityGraph): { from: number; to: number } {
  let from = Date.parse(graph.from);
  let to = Date.parse(graph.to);
  for (const n of graph.nodes) {
    for (const t of n.times ?? [n.firstAt, n.lastAt].filter((x): x is string => !!x)) {
      const ms = Date.parse(t);
      if (!Number.isFinite(ms)) continue;
      if (!Number.isFinite(from) || ms < from) from = ms;
      if (!Number.isFinite(to) || ms > to) to = ms;
    }
  }
  if (!Number.isFinite(from)) from = 0;
  if (!Number.isFinite(to) || to <= from) to = from + 1;
  return { from, to };
}

/** The scrubber's window as times, from its two handles (0–1 along the span). */
export function windowTimes(span: { from: number; to: number }, a: number, b: number): { from: number; to: number } {
  const lo = Math.max(0, Math.min(1, Math.min(a, b)));
  const hi = Math.max(0, Math.min(1, Math.max(a, b)));
  const len = span.to - span.from;
  return { from: span.from + lo * len, to: span.from + hi * len };
}

/** Whether a node happened inside a window; a node with no times (the run) always does. */
export function inWindow(node: ActivityNode, win: { from: number; to: number }): boolean {
  const times = (node.times ?? []).map((t) => Date.parse(t)).filter(Number.isFinite);
  if (times.length) return times.some((t) => t >= win.from && t <= win.to);
  const first = node.firstAt ? Date.parse(node.firstAt) : NaN;
  const last = node.lastAt ? Date.parse(node.lastAt) : first;
  if (!Number.isFinite(first)) return true;
  return first <= win.to && (Number.isFinite(last) ? last : first) >= win.from;
}

/** True when the window is the whole span (nothing dimmed). */
export const wholeWindow = (a: number, b: number): boolean => Math.min(a, b) <= 0 && Math.max(a, b) >= 1;

// ── Layout ──────────────────────────────────────────────────────────────

/** The widest a column gets; each is as wide as its longest (cut) label needs. */
export const COLUMN_WIDTH = 230;
/** The run's column: its title is drawn under it, short (the panel names it in full). */
export const RUN_COLUMN_WIDTH = 150;
/** How far into its column the run sits, so its title, centred under it, starts inside the picture. */
const RUN_INSET = 44;
/** About how wide a character of a 12 px label is. */
const CHAR_PX = 6.4;
export const ROW_HEIGHT = 30;
export const AGENT_ROW_HEIGHT = 48;
export const PAD = 40;
export const LABEL_CHARS = 28;

export interface PlacedNode {
  node: ActivityNode;
  x: number;
  y: number;
  r: number;
  column: number;
}

export interface PlacedEdge {
  from: string;
  to: string;
  kind: 'part' | 'sequence' | 'touched';
  count?: number;
  /** The SVG path. */
  d: string;
  /** Stroke width by count. */
  width: number;
}

export interface GraphLayout {
  nodes: PlacedNode[];
  edges: PlacedEdge[];
  width: number;
  height: number;
  /** Each drawn column's heading and x. */
  columns: Array<{ x: number; label: string }>;
}

const COLUMN_LABEL = (kinds: readonly ActivityNodeKind[]): string => (kinds.length === 1 ? KIND_LABEL[kinds[0]] : kinds[0] === 'question' ? 'Questions and approvals' : 'Resets, hand-overs, verdicts');

/** A label cut to fit beside its node (the full one goes in a title). */
export function shortLabel(label: string, max = LABEL_CHARS): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

/** Where every visible node goes. Deterministic for the same graph and filters. */
export function layoutActivityGraph(graph: ActivityGraph, groups: ReadonlySet<FilterGroupId> = ALL_GROUPS): GraphLayout {
  const { nodes, edges } = visibleGraph(graph, groups);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  // Agents in turn order: the order of the run's `part` edges, then any the edges did not name.
  const agentOrder: string[] = [];
  for (const e of edges) if (e.kind === 'part' && byId.get(e.to)?.kind === 'agent' && !agentOrder.includes(e.to)) agentOrder.push(e.to);
  for (const n of nodes) if (n.kind === 'agent' && !agentOrder.includes(n.id)) agentOrder.push(n.id);
  const agentRow = new Map(agentOrder.map((id, i) => [id, i]));

  // Which agents touched each leaf, for its barycentre.
  const touchers = new Map<string, number[]>();
  for (const e of edges) {
    if (e.kind !== 'touched') continue;
    const row = agentRow.get(e.from);
    if (row === undefined) continue;
    touchers.set(e.to, [...(touchers.get(e.to) ?? []), row]);
  }
  const bary = (id: string): number => {
    const rows = touchers.get(id);
    return rows?.length ? rows.reduce((s, r) => s + r, 0) / rows.length : Number.MAX_SAFE_INTEGER;
  };

  // Columns that have something in them.
  const columns: Array<{ kinds: readonly ActivityNodeKind[]; nodes: ActivityNode[] }> = [];
  for (const kinds of COLUMNS) {
    let list: ActivityNode[];
    if (kinds[0] === 'agent') list = agentOrder.map((id) => byId.get(id)!).filter(Boolean);
    else
      list = nodes
        .filter((n) => kinds.includes(n.kind))
        .sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind) || bary(a.id) - bary(b.id) || b.count - a.count || a.label.localeCompare(b.label));
    if (list.length) columns.push({ kinds, nodes: list });
  }

  const rowH = (kinds: readonly ActivityNodeKind[]) => (kinds[0] === 'agent' || kinds[0] === 'run' ? AGENT_ROW_HEIGHT : ROW_HEIGHT);
  const tallest = Math.max(1, ...columns.map((c) => c.nodes.length * rowH(c.kinds)));
  const height = tallest + PAD * 2;

  // Sizes: a leaf by its count against the busiest leaf; an agent by its tokens against the busiest agent.
  const leafMax = Math.max(1, ...nodes.filter((n) => n.kind !== 'run' && n.kind !== 'agent').map((n) => n.count));
  const tokenMax = Math.max(1, ...nodes.filter((n) => n.kind === 'agent').map((n) => n.tokens ?? 0));
  const radius = (n: ActivityNode): number => {
    if (n.kind === 'run') return 14;
    if (n.kind === 'agent') return n.tokens ? 9 + Math.round(9 * Math.sqrt(n.tokens / tokenMax)) : 12;
    return 4 + Math.round(6 * Math.sqrt(n.count / leafMax));
  };

  // Each column as wide as its labels need, so a graph of short names is not mostly gaps.
  const xs: number[] = [];
  let x = PAD;
  for (const c of columns) {
    xs.push(x);
    const need = c.kinds[0] === 'run' ? RUN_COLUMN_WIDTH : Math.max(...c.nodes.map((n) => shortLabel(n.label).length * CHAR_PX + radius(n))) + 40;
    x += Math.min(COLUMN_WIDTH, Math.max(110, Math.round(need)));
  }
  const width = Math.max(1, x - PAD) + PAD;

  const placed: PlacedNode[] = [];
  columns.forEach((c, ci) => {
    const h = rowH(c.kinds);
    const top = PAD + (tallest - c.nodes.length * h) / 2 + h / 2;
    c.nodes.forEach((n, i) => placed.push({ node: n, x: xs[ci] + (n.kind === 'run' ? RUN_INSET : 0), y: Math.round(top + i * h), r: radius(n), column: ci }));
  });
  const at = new Map(placed.map((p) => [p.node.id, p]));

  const countMax = Math.max(1, ...edges.map((e) => e.count ?? 1));
  const placedEdges: PlacedEdge[] = [];
  for (const e of edges) {
    const a = at.get(e.from);
    const b = at.get(e.to);
    if (!a || !b) continue;
    let d: string;
    if (e.kind === 'sequence' && a.column === b.column) {
      // Turn order down the agents' column: an arc to the left of it.
      const bend = a.x - 28 - Math.min(60, Math.abs(b.y - a.y) / 4);
      d = `M ${a.x - a.r} ${a.y} C ${bend} ${a.y}, ${bend} ${b.y}, ${b.x - b.r} ${b.y}`;
    } else {
      const [l, r] = a.x <= b.x ? [a, b] : [b, a];
      const x1 = l.x + l.r;
      const x2 = r.x - r.r;
      const mx = (x1 + x2) / 2;
      d = `M ${x1} ${l.y} C ${mx} ${l.y}, ${mx} ${r.y}, ${x2} ${r.y}`;
    }
    placedEdges.push({ from: e.from, to: e.to, kind: e.kind, ...(e.count !== undefined ? { count: e.count } : {}), d, width: e.kind === 'touched' ? 1 + Math.round(2 * Math.sqrt((e.count ?? 1) / countMax) * 10) / 10 : 1.5 });
  }

  return { nodes: placed, edges: placedEdges, width, height, columns: columns.map((c, i) => ({ x: xs[i], label: COLUMN_LABEL(c.kinds) })) };
}

/** Where a node's label goes: beside it, or for the run, under it and shorter. */
export function labelPlace(p: PlacedNode): { x: number; y: number; anchor: 'start' | 'middle'; chars: number } {
  if (p.node.kind === 'run') return { x: p.x, y: p.y + p.r + 16, anchor: 'middle', chars: 16 };
  return { x: p.x + p.r + 6, y: p.y + 4, anchor: 'start', chars: LABEL_CHARS };
}

/** The neighbours of a node (what to light when it is selected). */
export function neighbours(layout: GraphLayout, id: string): Set<string> {
  const out = new Set<string>([id]);
  for (const e of layout.edges) {
    if (e.from === id) out.add(e.to);
    if (e.to === id) out.add(e.from);
  }
  return out;
}

/**
 * Keyboard movement between nodes: up and down a column, left and right to
 * the nearest node of the next column that has one, Home and End to the
 * first and last. Undefined for any other key, or at an edge.
 */
export function nextNode(layout: GraphLayout, id: string, key: string): string | undefined {
  const here = layout.nodes.find((p) => p.node.id === id);
  if (!here) return layout.nodes[0]?.node.id;
  if (key === 'Home') return layout.nodes[0]?.node.id;
  if (key === 'End') return layout.nodes[layout.nodes.length - 1]?.node.id;
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    const col = layout.nodes.filter((p) => p.column === here.column).sort((a, b) => a.y - b.y);
    const i = col.indexOf(here) + (key === 'ArrowUp' ? -1 : 1);
    return col[i]?.node.id;
  }
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const step = key === 'ArrowLeft' ? -1 : 1;
    const columns = [...new Set(layout.nodes.map((p) => p.column))].sort((a, b) => a - b);
    const target = columns[columns.indexOf(here.column) + step];
    if (target === undefined) return undefined;
    let best: PlacedNode | undefined;
    for (const p of layout.nodes) if (p.column === target && (!best || Math.abs(p.y - here.y) < Math.abs(best.y - here.y))) best = p;
    return best?.node.id;
  }
  return undefined;
}

/** An SVG path or element for a node's shape, at a centre and radius. */
export function shapePath(shape: NodeShape, x: number, y: number, r: number): string {
  const f = (n: number) => Math.round(n * 10) / 10;
  switch (shape) {
    case 'circle':
      return `M ${f(x - r)} ${f(y)} a ${f(r)} ${f(r)} 0 1 0 ${f(2 * r)} 0 a ${f(r)} ${f(r)} 0 1 0 ${f(-2 * r)} 0 Z`;
    case 'square':
      return `M ${f(x - r * 0.9)} ${f(y - r * 0.9)} h ${f(r * 1.8)} v ${f(r * 1.8)} h ${f(-r * 1.8)} Z`;
    case 'roundrect': {
      const w = r * 1.4;
      const h = r;
      const c = r * 0.35;
      return `M ${f(x - w + c)} ${f(y - h)} H ${f(x + w - c)} Q ${f(x + w)} ${f(y - h)} ${f(x + w)} ${f(y - h + c)} V ${f(y + h - c)} Q ${f(x + w)} ${f(y + h)} ${f(x + w - c)} ${f(y + h)} H ${f(x - w + c)} Q ${f(x - w)} ${f(y + h)} ${f(x - w)} ${f(y + h - c)} V ${f(y - h + c)} Q ${f(x - w)} ${f(y - h)} ${f(x - w + c)} ${f(y - h)} Z`;
    }
    case 'diamond':
      return `M ${f(x)} ${f(y - r * 1.2)} L ${f(x + r * 1.2)} ${f(y)} L ${f(x)} ${f(y + r * 1.2)} L ${f(x - r * 1.2)} ${f(y)} Z`;
    case 'hexagon': {
      const pts = [0, 1, 2, 3, 4, 5].map((i) => {
        const a = (Math.PI / 3) * i;
        return `${f(x + r * 1.1 * Math.cos(a))} ${f(y + r * 1.1 * Math.sin(a))}`;
      });
      return `M ${pts.join(' L ')} Z`;
    }
    case 'triangle':
      return `M ${f(x)} ${f(y - r * 1.15)} L ${f(x + r * 1.1)} ${f(y + r * 0.85)} L ${f(x - r * 1.1)} ${f(y + r * 0.85)} Z`;
  }
}

/** The legend: one entry per kind the picture shows, in column order. */
export function legendEntries(nodes: ReadonlyArray<{ kind: ActivityNodeKind }>): Array<{ kind: ActivityNodeKind; label: string }> {
  const present = new Set(nodes.map((n) => n.kind));
  return COLUMNS.flat()
    .filter((k) => present.has(k))
    .map((kind) => ({ kind, label: KIND_LABEL[kind] }));
}

/** A node's line for a screen reader and the detail pane. */
export function nodeSummary(n: ActivityNode): string {
  const parts = [KIND_ONE[n.kind], n.label];
  if (n.kind !== 'run' && n.kind !== 'agent') parts.push(n.count === 1 ? 'once' : `${n.count} times`);
  if (n.tokens) parts.push(`${n.tokens.toLocaleString('en')} tokens`);
  if (n.status) parts.push(n.status);
  return parts.join(', ');
}

// ── Export ──────────────────────────────────────────────────────────────

const xml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface ExportOptions {
  theme: GraphTheme;
  /** The scrubber's window; nodes outside it are drawn faint, as on screen. */
  window?: { from: number; to: number };
  /** A second line under the title (the time range, say). */
  subtitle?: string;
}

const HEADER = 56;
const LEGEND_ROW = 26;

/**
 * The picture as a standalone SVG: a title line, the graph as the filters
 * show it (nothing they hide), and the legend. Fonts are named, colours are
 * concrete, so it reads the same in any viewer and rasterises to a PNG.
 */
export function exportSvg(graph: ActivityGraph, layout: GraphLayout, opts: ExportOptions): string {
  const c = CHROME[opts.theme];
  const legend = legendEntries(layout.nodes.map((p) => p.node));
  const perRow = Math.max(1, Math.floor(layout.width / 170));
  const legendH = Math.ceil(legend.length / perRow) * LEGEND_ROW + 12;
  const width = Math.max(layout.width, 480);
  const height = HEADER + layout.height + legendH;
  const dim = (n: ActivityNode) => (opts.window && !inWindow(n, opts.window) ? 0.22 : 1);
  const out: string[] = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="Inter, system-ui, -apple-system, 'Segoe UI', sans-serif">`);
  out.push(`<rect width="${width}" height="${height}" fill="${c.surface}"/>`);
  out.push(`<text x="${PAD}" y="28" font-size="16" font-weight="600" fill="${c.ink}">${xml(graph.title)}</text>`);
  if (opts.subtitle) out.push(`<text x="${PAD}" y="46" font-size="12" fill="${c.secondary}">${xml(opts.subtitle)}</text>`);
  out.push(`<g transform="translate(0 ${HEADER})">`);
  for (const col of layout.columns) out.push(`<text x="${col.x - 10}" y="18" font-size="12" fill="${c.muted}">${xml(col.label)}</text>`);
  const byId = new Map(layout.nodes.map((p) => [p.node.id, p]));
  for (const e of layout.edges) {
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    const o = Math.min(a ? dim(a.node) : 1, b ? dim(b.node) : 1) * (e.kind === 'touched' ? 0.45 : 0.8);
    out.push(`<path d="${e.d}" fill="none" stroke="${c.edge}" stroke-width="${e.width}" opacity="${o}"${e.kind === 'sequence' ? ' stroke-dasharray="4 3"' : ''}/>`);
  }
  for (const p of layout.nodes) {
    const style = KIND_STYLE[p.node.kind];
    const colour = kindColour(p.node.kind, opts.theme);
    const fill = style.outline ? c.surface : colour;
    out.push(`<g opacity="${dim(p.node)}">`);
    out.push(`<path d="${shapePath(style.shape, p.x, p.y, p.r)}" fill="${fill}" stroke="${style.outline ? colour : c.surface}" stroke-width="${style.outline ? 2 : 1.5}"/>`);
    const at = labelPlace(p);
    out.push(`<text x="${at.x}" y="${at.y}" font-size="12"${at.anchor === 'middle' ? ' text-anchor="middle"' : ''} fill="${c.ink}">${xml(shortLabel(p.node.label, at.chars))}</text>`);
    out.push(`</g>`);
  }
  out.push(`</g>`);
  // The legend.
  const ly = HEADER + layout.height;
  legend.forEach((l, i) => {
    const x = PAD + (i % perRow) * 170;
    const y = ly + Math.floor(i / perRow) * LEGEND_ROW + 14;
    const style = KIND_STYLE[l.kind];
    const colour = kindColour(l.kind, opts.theme);
    out.push(`<path d="${shapePath(style.shape, x, y, 6)}" fill="${style.outline ? c.surface : colour}" stroke="${style.outline ? colour : c.surface}" stroke-width="${style.outline ? 2 : 1}"/>`);
    out.push(`<text x="${x + 14}" y="${y + 4}" font-size="12" fill="${c.secondary}">${xml(l.label)}</text>`);
  });
  out.push(`</svg>`);
  return out.join('\n');
}

/** A file name for the export: the title, slugged. */
export function exportName(graph: ActivityGraph, ext: 'svg' | 'png'): string {
  const slug = graph.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `activity-${slug || 'graph'}.${ext}`;
}
